import React, { useState, useEffect } from 'react';
import { CreditCard, TrendingUp, DollarSign, Users, CheckCircle, AlertCircle, Package, Edit } from 'lucide-react';
import axios from 'axios';

const SubscriptionManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    activeSubscriptions: 0,
    basicTenants: 0,
    premiumTenants: 0
  });
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch tenants and subscription plans in parallel
      const [tenantsRes, plansRes] = await Promise.all([
        axios.get(`${API_URL}/api/tenants/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/tenants/subscription-plans/list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const tenantsData = tenantsRes.data;
      const plansData = plansRes.data;
      
      setTenants(tenantsData);
      setSubscriptionPlans(plansData);

      // Create plans lookup by code
      const plansLookup = {};
      plansData.forEach(p => {
        plansLookup[p.code] = p;
        plansLookup[p.name] = p; // Also lookup by name for backward compatibility
      });

      // Calculate stats
      const revenue = tenantsData.reduce((sum, t) => {
        const plan = plansLookup[t.subscription_plan];
        return sum + (plan?.price_monthly || 0);
      }, 0);

      setStats({
        totalRevenue: revenue,
        activeSubscriptions: tenantsData.filter(t => t.is_active && t.subscription_plan !== 'basic' && t.subscription_plan !== 'free').length,
        basicTenants: tenantsData.filter(t => t.subscription_plan === 'basic' || t.subscription_plan === 'free').length,
        premiumTenants: tenantsData.filter(t => t.subscription_plan === 'premium').length
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanInfo = (planCode) => {
    // Find plan by code or name
    const plan = subscriptionPlans.find(p => p.code === planCode || p.name === planCode);
    if (plan) return plan;
    
    // Return default if not found
    return {
      name: planCode || 'Unknown',
      code: planCode,
      price_monthly: 0,
      max_students: 100,
      max_staff: 20,
      max_configurable_modules: 0
    };
  };

  const handleUpgrade = (tenant) => {
    setSelectedTenant(tenant);
    setShowUpgradeModal(true);
  };

  const handlePlanChange = async (newPlanCode) => {
    try {
      const token = localStorage.getItem('token');
      const newPlan = getPlanInfo(newPlanCode);
      
      await axios.put(
        `${API_URL}/api/tenants/${selectedTenant.id}`,
        { 
          subscription_plan: newPlanCode,
          max_students: newPlan.max_students,
          max_staff: newPlan.max_staff
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      alert(`Subscription updated to ${newPlan.name} plan successfully!`);
      setShowUpgradeModal(false);
      fetchData();
    } catch (error) {
      alert('Failed to update subscription');
    }
  };

  const getPlanBadgeColor = (planCode) => {
    const colors = {
      free: 'bg-gray-100 text-gray-800 border-gray-300',
      basic: 'bg-gray-100 text-gray-800 border-gray-300',
      standard: 'bg-blue-100 text-blue-800 border-blue-300',
      premium: 'bg-purple-100 text-purple-800 border-purple-300'
    };
    return colors[planCode?.toLowerCase()] || colors.basic;
  };

  const getPlanHeaderColor = (planCode) => {
    const colors = {
      basic: 'from-gray-500 to-gray-600',
      standard: 'from-blue-500 to-blue-600',
      premium: 'from-purple-500 to-indigo-600'
    };
    return colors[planCode] || colors.basic;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
        <p className="text-gray-600 mt-1">Monitor and manage tenant subscriptions</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Monthly Revenue</p>
              <p className="text-2xl font-bold text-gray-900">₹{stats.totalRevenue.toLocaleString('en-IN')}</p>
            </div>
            <DollarSign className="h-10 w-10 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Paid Subscriptions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeSubscriptions}</p>
            </div>
            <CreditCard className="h-10 w-10 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Basic/Free Tenants</p>
              <p className="text-2xl font-bold text-gray-900">{stats.basicTenants}</p>
            </div>
            <Users className="h-10 w-10 text-orange-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Premium Tenants</p>
              <p className="text-2xl font-bold text-gray-900">{stats.premiumTenants}</p>
            </div>
            <TrendingUp className="h-10 w-10 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Subscription Plans Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {subscriptionPlans.map((plan) => {
          const count = tenants.filter(t => 
            t.subscription_plan === plan.code || t.subscription_plan === plan.name
          ).length;
          
          return (
            <div 
              key={plan.id} 
              className="bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <div className={`bg-gradient-to-r ${getPlanHeaderColor(plan.code)} text-white p-4`}>
                <h3 className="text-xl font-bold">{plan.name} Plan</h3>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  {plan.price_monthly > 0 ? (
                    <p className="text-3xl font-bold text-gray-900">
                      ₹{plan.price_monthly.toLocaleString('en-IN')}
                      <span className="text-sm text-gray-500">/month</span>
                    </p>
                  ) : (
                    <p className="text-3xl font-bold text-gray-900">Free</p>
                  )}
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="h-4 w-4 text-blue-500" />
                    {plan.max_students === -1 ? 'Unlimited' : plan.max_students} Students
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="h-4 w-4 text-green-500" />
                    {plan.max_staff === -1 ? 'Unlimited' : plan.max_staff} Staff
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Package className="h-4 w-4 text-purple-500" />
                    {plan.max_configurable_modules === -1 
                      ? 'Unlimited Modules' 
                      : plan.max_configurable_modules === 0 
                        ? 'Fixed Modules Only'
                        : `${plan.max_configurable_modules} Modules`
                    }
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900 text-lg">{count}</span> tenant{count !== 1 ? 's' : ''} on this plan
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tenants Subscription Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Tenant Subscriptions</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-700">Organization</th>
                <th className="text-left p-4 font-semibold text-gray-700">Current Plan</th>
                <th className="text-left p-4 font-semibold text-gray-700">Modules</th>
                <th className="text-left p-4 font-semibold text-gray-700">Revenue</th>
                <th className="text-left p-4 font-semibold text-gray-700">Status</th>
                <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {tenants.map((tenant) => {
                const plan = getPlanInfo(tenant.subscription_plan);
                const moduleCount = tenant.enabled_modules?.length || 0;
                
                return (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-gray-900">{tenant.name}</p>
                        <p className="text-sm text-gray-500">{tenant.subdomain}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPlanBadgeColor(tenant.subscription_plan)}`}>
                        {plan.name.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1.5">
                        <Package className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">{moduleCount} modules</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-gray-900">
                        {plan.price_monthly > 0 ? `₹${plan.price_monthly.toLocaleString('en-IN')}/mo` : 'Free'}
                      </p>
                    </td>
                    <td className="p-4">
                      {tenant.is_active ? (
                        <span className="flex items-center gap-1.5 text-green-600 text-sm">
                          <CheckCircle className="h-4 w-4" />
                          Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-red-600 text-sm">
                          <AlertCircle className="h-4 w-4" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => handleUpgrade(tenant)}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5"
                      >
                        <Edit className="h-3.5 w-3.5" />
                        Change Plan
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                Change Plan for {selectedTenant.name}
              </h2>
              <p className="text-gray-500 mt-1">
                Current Plan: <span className="font-medium">{getPlanInfo(selectedTenant.subscription_plan).name}</span>
              </p>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4">
                {subscriptionPlans.map((plan) => {
                  const isCurrentPlan = selectedTenant.subscription_plan === plan.code || 
                                       selectedTenant.subscription_plan === plan.name;
                  return (
                    <button
                      key={plan.id}
                      onClick={() => !isCurrentPlan && handlePlanChange(plan.code)}
                      disabled={isCurrentPlan}
                      className={`p-4 border-2 rounded-xl text-left transition-all ${
                        isCurrentPlan 
                          ? 'border-green-500 bg-green-50 cursor-default' 
                          : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <h3 className="font-bold text-gray-900">{plan.name}</h3>
                      <p className="text-lg font-bold mt-1">
                        {plan.price_monthly > 0 ? `₹${plan.price_monthly.toLocaleString('en-IN')}/mo` : 'Free'}
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        {plan.max_configurable_modules === -1 
                          ? 'Unlimited Modules' 
                          : plan.max_configurable_modules === 0 
                            ? 'Fixed Modules Only'
                            : `${plan.max_configurable_modules} Modules`
                        }
                      </p>
                      {isCurrentPlan && (
                        <p className="text-green-600 text-sm font-medium mt-2">✓ Current Plan</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
